import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { Plus, Rocket } from 'lucide-react';

import { getMigrationGroups } from '../api/migrationGroups';
import { getMigrations } from '../api/migrations';
import GroupMetricsMatrix from '../components/GroupMetricsMatrix';
import GroupsTable, { GroupsTableSkeleton } from '../components/GroupsTable';
import GroupsToolbar from '../components/GroupsToolbar';
import { HeroBand } from '../components/abPrimitives';
import { GroupState, filterGroups, groupsToCsv } from '../lib/groupRollup';
import { STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-280 (FD-ADR-031) — the groups workspace, and the console's landing page.
//
// FD-472 (R-FD-048) replaced the nested expand-in-place grid with a flat table,
// at the owner's request and to match the AirBridge design board. What the
// accordion bought and this gives up is recorded on `GroupsTable`.

const STATE_PARAM = 'state';
const QUERY_PARAM = 'q';
const TENANT_PARAM = 'tenant';

/**
 * First run — no groups AND no campaigns.
 *
 * The distinction matters. "No groups" alone is ORDINARY: groups are optional
 * (FD-ADR-030), most campaigns will never be in one, and an empty-state that
 * implies an unfiled campaign is wrong would push operators into busywork. But
 * "no groups and no campaigns" means the deployment has never been used, and the
 * useful thing to offer there is the campaign path, not the group path.
 */
function FirstRun({ onCreateMigration }: { onCreateMigration: () => void }) {
  return (
    <div
      className="flex flex-col items-center gap-4 rounded-lg border px-6 py-16 text-center"
      style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
    >
      <Rocket size={28} strokeWidth={1.5} style={{ color: TEXT.disabled }} />
      <div className="flex max-w-md flex-col gap-2">
        <h2 className="font-display text-lg font-semibold" style={{ color: TEXT.primary }}>
          Nothing has been migrated here yet
        </h2>
        <p className="font-display text-sm leading-relaxed" style={{ color: TEXT.secondary }}>
          Start with a campaign — a set of devices moving to one destination. Groups are for
          organizing campaigns once you have several, so there is nothing to file yet.
        </p>
      </div>
      <Button
        appearance="primary"
        icon={<Plus size={16} strokeWidth={1.5} />}
        onClick={onCreateMigration}
      >
        New campaign
      </Button>
    </div>
  );
}

/**
 * Hand the filtered rows to the browser as a file.
 *
 * The `IdentityMappingCsvControls` pattern — a Blob and a synthetic anchor. No
 * endpoint is involved because no endpoint is needed: the data is already
 * loaded, and asking the server to re-render what the client is displaying
 * would be a second source of truth for the same table.
 */
function downloadCsv(csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `airbridge-groups-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function GroupsListPage() {
  const navigate = useNavigate();

  // URL, never `localStorage` — the rule that dates to ADR-015, survived
  // ADR-031 and survived FD-ADR-040's environment selector. A filtered view an
  // operator can send to a colleague is the whole point, and a stored filter
  // that silently outlives the session is how someone reads a subset believing
  // it is everything.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get(QUERY_PARAM) ?? '';
  const state = (searchParams.get(STATE_PARAM) as GroupState | null) ?? undefined;
  const tenantId = searchParams.get(TENANT_PARAM) ?? undefined;

  const setParam = useCallback(
    (key: string, value?: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const groupsQuery = useQuery({
    queryKey: ['migration-groups'],
    queryFn: getMigrationGroups,
  });

  // Only to tell "no groups" (ordinary) from "nothing at all" (first run). One
  // row is enough to answer that, so this asks for one — and it shares the key
  // shape the list page uses, so it is not a duplicate of a heavier query.
  const anyMigrationsQuery = useQuery({
    queryKey: ['migrations', { pageSize: 1 }],
    queryFn: () => getMigrations({ pageSize: 1 }),
    enabled: (groupsQuery.data?.groups.length ?? 0) === 0 && groupsQuery.isSuccess,
  });

  const data = groupsQuery.data;
  const groups = useMemo(() => data?.groups ?? [], [data]);
  const visible = useMemo(
    () => filterGroups(groups, { query, state, tenantId }),
    [groups, query, state, tenantId],
  );
  const isFiltered = query.trim() !== '' || state !== undefined || tenantId !== undefined;

  const hasAnyMigration = (anyMigrationsQuery.data?.migrations.length ?? 0) > 0;
  const showFirstRun =
    groupsQuery.isSuccess &&
    groups.length === 0 &&
    anyMigrationsQuery.isSuccess &&
    !hasAnyMigration;

  const openGroup = useCallback(
    (id: string) => navigate(`/groups/${encodeURIComponent(id)}/overview`),
    [navigate],
  );
  const openCampaigns = useCallback(
    (id: string) => navigate(`/groups/${encodeURIComponent(id)}/migrations`),
    [navigate],
  );
  const openSettings = useCallback(
    (id: string) => navigate(`/groups/${encodeURIComponent(id)}/settings`),
    [navigate],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* FD-467 — the AirBridge hero. The subtitle keeps saying that groups are
          INERT (FD-ADR-030 §3), because an operator who believes assigning a
          group changes how a device migrates will hesitate to reorganise, and
          the whole value of the container is that reorganising is free. */}
      <HeroBand
        eyebrow="Migration"
        title="Groups"
        subtitle="Organize campaigns by customer, by tenant, or however you run your engagements. Groups are optional and change nothing about how a migration runs."
      />

      {/* NO PageActions HERE (FD-481). FD-470 portalled "New campaign" and
          "New group" into the top bar from this page; both are now entries on
          the shell's persistent `New` menu, reachable from every route. Keeping
          them here as well would put "New group" on screen twice on the one
          page that has it.

          The SLOT is not retired — it is the shell's facility for a page's own
          primary action, which a global create is not. This page simply has no
          such action today. */}

      {groupsQuery.isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not load groups</MessageBarTitle>
            {groupsQuery.error instanceof Error ? groupsQuery.error.message : 'Unexpected error'}
          </MessageBarBody>
          <MessageBarActions>
            <Button size="small" onClick={() => groupsQuery.refetch()}>
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}

      {data && <GroupMetricsMatrix data={data} />}

      {groupsQuery.isLoading && <GroupsTableSkeleton />}

      {showFirstRun && <FirstRun onCreateMigration={() => navigate('/migrations/new')} />}

      {!groupsQuery.isLoading && !showFirstRun && data && (
        <>
          {/* The toolbar is offered only once there is something to filter. On a
              deployment with two groups it is chrome that cannot help. */}
          {groups.length > 1 && (
            <GroupsToolbar
              allGroups={groups}
              visibleCount={visible.length}
              query={query}
              onQueryChange={(v) => setParam(QUERY_PARAM, v)}
              state={state}
              onStateChange={(v) => setParam(STATE_PARAM, v)}
              tenantId={tenantId}
              onTenantChange={(v) => setParam(TENANT_PARAM, v)}
              onExport={() => downloadCsv(groupsToCsv(visible))}
            />
          )}

          <GroupsTable
            groups={visible}
            isFiltered={isFiltered}
            onOpenGroup={openGroup}
            onOpenCampaigns={openCampaigns}
            onOpenSettings={openSettings}
          />

          {groups.length === 0 && (
            // Groups are optional, so this is guidance rather than a problem to
            // fix — and it links to the unfiled view, which is the thing an
            // operator with campaigns but no groups actually wants.
            <MessageBar intent="info">
              <MessageBarBody>
                <MessageBarTitle>No groups yet</MessageBarTitle>
                Groups are optional. Create one to organize related campaigns — for example, every
                campaign for one customer.
              </MessageBarBody>
              <MessageBarActions>
                <Button size="small" onClick={() => navigate('/migrations?group=none')}>
                  View ungrouped campaigns
                </Button>
              </MessageBarActions>
            </MessageBar>
          )}
        </>
      )}
    </div>
  );
}
